import { useEffect, useState, useCallback, useRef, useLayoutEffect, ElementType } from 'react'

import Scenes, { SceneValues } from 'util/Scenes'
import LoginEmailScene from 'components/login/LoginEmailScene'
import { createRegistrationPath, DASHBOARD, REGISTRATION, SSO_REDIRECT } from 'routes/paths'
import LoginPasswordScene from 'components/login/LoginPasswordScene'
import AccessCodeScene from 'components/login/AccessCodeScene'
import ExpiredPasswordScene from 'components/login/ExpiredPasswordScene'
import EntryCentralGirlUrl from 'assets/images/entry_central_girl.svg'
import SupportIcon from '@ui-components-3/icons/lib/duotone/UserHeadset'
import TopLeafUrl from 'assets/images/top_left_leaf.svg'
import BottomLeafUrl from 'assets/images/bottom_right_leaf.svg'
import TopBubblesUrl from 'assets/images/top_right_bubbles.svg'
import AppBadges from 'components/login/AppBadges'
import EntryFooter from 'components/login/EntryFooter'
import { SUPPORT_NUMBER } from 'util/support'
import { FormikHelpers } from 'formik'
import type { Dispatch } from 'redux'
import ForgotPasswordScene from 'components/login/ForgotPasswordScene'
import { APP_NAME } from 'util/constants'
import ApplicationLogo from 'components/ApplicationLogo'
import { LogoVariants } from 'components/ApplicationLogo/ApplicationLogo'
import { GenerateMfaValues } from 'schemas/GenerateMfaSchema'
import GenerateMfaScene from 'components/login/GenerateMfaScene'
import { buildMfaChoice } from 'util/mfa'
import VerifyMfaScene from 'components/login/VerifyMfaScene'
import { VerifyMfaValues } from 'schemas/VerifyMfaSchema'
import useMfaSession from 'hooks/useMfaSession'
import VerifyCreateAccountScene from 'components/login/VerifyCreateAccountScene/VerifyCreateAccountScene'
import moment from 'moment'
import { AuthChallenge, AuthChallengeValues } from 'types/authChallenge'
import { VerifyCreateAccountValues } from 'schemas/VerifyCreateAccountSchema'
import { LookUpSuccessfulResponses } from 'types/account/lookupResponses'
import { useHistory, useLocation } from 'react-router'
import { useDispatch } from 'react-redux'
import { setSource } from 'redux/slices/api'
import CookiesAgreementDialog from 'components/login/CookiesAgreementDialog'
import { ModalRef } from '@ui-components-3/ui/lib/components/Modal'
import RouteBranch from 'routes/RouteBranch'
import { localPersistence, sessionPersistence } from 'util/storage'
import { COOKIES_POLICY_AGREEMENT } from 'util/local-constants'
import { PASSWORD_EXPIRED } from 'util/session-constants'

export type EmailValues = { email: string }
export type PwValues = { password: string }
export type UpdatePwValues = { oldPassword: string; password: string; confirmPassword: string }
export type CodeValues = { code: string }

type LocationState = { referrer?: string }

type Route = { component: ElementType; path: string; exact: boolean; crumb: () => any; routes?: Route[] }

export type EntryPageProps = {
  fetchMe: () => Promise<(dispatch: Dispatch) => Promise<{ success: boolean }>>
  query?: { email: string; auto: string; source: string }
  attemptLogin: (
    email: string,
    password: string,
  ) => Promise<(dispatch: Dispatch) => Promise<{ success: boolean; nextScene: string }>>
  updatePassword: (
    oldPassword: string,
    password: string,
  ) => Promise<(dispatch: Dispatch) => Promise<{ success: boolean }>>
  forgotPassword: (email: string) => Promise<(dispatch: Dispatch) => Promise<{ success: boolean }>>
  verifyAccessCode: (code: string) => Promise<(dispatch: Dispatch) => Promise<{ success: boolean }>>
  fetchAuthChallenge: (email: string) => Promise<
    (dispatch: Dispatch) => Promise<{
      success: boolean
      data: { authChallenge: AuthChallengeValues; redirectUrl?: string }
    }>
  >
  // TODO: Replace type once api thunk is converted to typescript
  lookupMemberRecord: (
    email: string,
    dob?: string,
  ) => Promise<(dispatch: Dispatch) => Promise<{ success: boolean; data: LookUpSuccessfulResponses | null }>>
  isSendingForgotPw: boolean
  generateMfa: (mfaChoice: {
    deliveryMethod: string
    phoneId?: string
  }) => Promise<(dispatch: Dispatch) => Promise<{ success: boolean }>>
  verifyMfa: (mfaCode: string) => Promise<(dispatch: Dispatch) => Promise<{ success: boolean; nextScene: string }>>
  mfaOptions: Record<string, string>[]
  routes?: Route[]
}

const EntryPage = (props: EntryPageProps) => {
  const {
    fetchMe,
    query,
    attemptLogin,
    updatePassword,
    forgotPassword,
    verifyAccessCode,
    fetchAuthChallenge,
    lookupMemberRecord,
    generateMfa,
    verifyMfa,
    mfaOptions,
    routes,
  } = props

  const CookiesModalRef = useRef<ModalRef>(null)
  const location = useLocation<LocationState>()
  const dispatch = useDispatch()

  const history = useHistory()

  const [scene, setScene] = useState<SceneValues>(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('scene') === 'expired-password') {
      return Scenes.login.expiredPassword
    }
    return Scenes.login.email
  })
  const [email, setEmail] = useState<string | null>(null)
  const [dob, setDob] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [previousMfaChoice, setPreviousMfaChoice] = useState<{ deliveryMethod: string; phoneId?: string } | null>(null)
  const hasVisited = localPersistence.get(COOKIES_POLICY_AGREEMENT)

  const handleBackToEmailScene = useCallback(() => {
    setScene(Scenes.login.email)
  }, [])

  // Keep track of MFA session expiration and return to email scene if expired
  useMfaSession(scene, handleBackToEmailScene)

  useLayoutEffect(() => {
    if (!hasVisited) {
      CookiesModalRef.current?.open()
    }
  }, [hasVisited])

  const handleCookiesAgreement = useCallback(() => {
    localPersistence.set(COOKIES_POLICY_AGREEMENT, '1')
  }, [])

  const redirectToSSOPage = (redirectUrl: string) => {
    let path = SSO_REDIRECT
    if (location?.search) {
      const searchParams = new URLSearchParams(location.search)
      searchParams.delete('auto')
      const cleanSearch = searchParams.toString()
      path += cleanSearch ? `?${cleanSearch}` : ''
    }
    history.push(path, { referrer: location?.state?.referrer, redirectUrl })
  }

  const handleEmailSubmit = async (
    { email }: EmailValues,
    form: FormikHelpers<EmailValues> | { resetForm: () => void },
  ) => {
    const lowerEmail = email.toLowerCase()
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { success, data } = await fetchAuthChallenge(lowerEmail)
    const { redirectUrl, authChallenge } = data ?? {}
    if (success) {
      setEmail(lowerEmail)
      form.resetForm()
      if (authChallenge === AuthChallenge.password) {
        setScene(Scenes.login.password)
      } else if (authChallenge === AuthChallenge.sso && !!redirectUrl) {
        redirectToSSOPage(redirectUrl)
      }
    } else {
      setScene(Scenes.login.email)
    }
  }

  useEffect(() => {
    if (query?.source) {
      dispatch(setSource(query?.source))
    }
    if (query?.email) {
      if ('auto' in query && +query.auto) {
        handleEmailSubmit({ email: query?.email }, { resetForm: () => null })
      }
    }

    /*
      TODO: handleEmailSubmit needs to be wrapped in useCallback otherwise adding it 
      to dependency array here will cause infinite loop
    */
    // eslint-disable-next-line
  }, [query, dispatch])

  const handlePasswordSubmit = async ({ password }: PwValues, form: FormikHelpers<PwValues>) => {
    setPassword(password)
    // Source needs to be set here as well incase handleSessionEnd was called during MFA login
    if (query?.source) {
      dispatch(setSource(query?.source))
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { success, nextScene } = await attemptLogin(email, password)
    if (success) {
      if (nextScene === Scenes.login.expiredPassword) {
        setScene(nextScene)
      } else if (nextScene === Scenes.login.generateMfa) {
        setScene(nextScene)
      } else {
        form.resetForm()
        if (location?.state?.referrer) {
          history.push(location.state.referrer)
        } else {
          history.push(DASHBOARD)
        }
      }
    }
  }

  const handleUpdatePasswordSubmit = async (
    { oldPassword, password }: UpdatePwValues,
    form: FormikHelpers<UpdatePwValues>,
  ) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { success } = await updatePassword(oldPassword, password)
    if (success) {
      sessionPersistence.set(PASSWORD_EXPIRED, 'false')
      await fetchMe()
      form.resetForm()
      if (location?.state?.referrer) {
        history.push(location.state.referrer)
      } else {
        history.push(DASHBOARD)
      }
    }
  }

  const handleAccessCodeSubmit = async (values: CodeValues, form: FormikHelpers<CodeValues>) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { success } = await verifyAccessCode(values.code)
    if (success) {
      form.resetForm()
      history.push(createRegistrationPath(REGISTRATION, values.code, { email, dob }))
    }
  }

  const handleForgotPassword = async () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    await forgotPassword(email)
  }

  const handleVerifyMfa = async (values: VerifyMfaValues, form: FormikHelpers<VerifyMfaValues>) => {
    // TODO: Remove once api thunk is typed
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { success, nextScene } = await verifyMfa(values.mfaCode)
    if (success) {
      if (nextScene === Scenes.login.expiredPassword) {
        setScene(nextScene)
      } else {
        form.resetForm()
        if (location?.state?.referrer) {
          history.push(location.state.referrer)
        } else {
          history.push(DASHBOARD)
        }
      }
    }
  }

  const handleVerifyCreateAccount = async ({ email, dob }: VerifyCreateAccountValues) => {
    const dobDate = moment(dob, 'MM-DD-YYYY')
    const formattedDob = dobDate.format('YYYY-MM-DD')
    const lowerEmail = email.toLowerCase()

    // TODO: Remove once api thunk is typed
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { success: authChallengeSuccess, data: authChallengeData } = await fetchAuthChallenge(lowerEmail)
    const { redirectUrl, authChallenge } = authChallengeData ?? {}
    if (authChallengeSuccess && authChallenge === AuthChallenge.sso && !!redirectUrl) {
      redirectToSSOPage(redirectUrl)
      return
    }

    // TODO: Remove once api thunk is typed
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { success, data } = await lookupMemberRecord({ email: lowerEmail, dob: formattedDob })
    if (success) {
      // If member record does not exist
      if (data?.statusCode === 100) {
        setEmail(lowerEmail)
        setDob(dobDate.format('MM-DD-YYYY'))
        setScene(Scenes.login.accessCode)
        // If member record does exist
      } else if (data?.memberId) {
        const registrationPath = createRegistrationPath(REGISTRATION, data.memberId, {
          email: lowerEmail,
          dob,
        })
        history.push(registrationPath)
      }
    }
  }

  const handleGenerateMfa = async (values: GenerateMfaValues, form: FormikHelpers<GenerateMfaValues>) => {
    const payload = buildMfaChoice(values.deliveryMethod)
    setPreviousMfaChoice(payload)
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { success } = await generateMfa(payload)
    if (success) {
      form.resetForm()
      setScene(Scenes.login.verifyMfa)
    }
  }

  const handleResendGenerateMfa = async () => {
    if (previousMfaChoice) await generateMfa(previousMfaChoice)
    else setScene(Scenes.login.generateMfa)
  }

  const handleForgotPasswordClick = () => setScene(Scenes.login.forgotPassword)
  const handleBackToGenerateMfa = useCallback(() => setScene(Scenes.login.generateMfa), [])
  const handleCreateAccount = useCallback(() => setScene(Scenes.login.createAccount), [])

  const viewForScene = (scene: SceneValues) => {
    switch (scene) {
      case Scenes.login.password:
        return (
          <LoginPasswordScene
            onBack={handleBackToEmailScene}
            onForgotPassword={handleForgotPasswordClick}
            onSubmit={handlePasswordSubmit}
          />
        )
      case Scenes.login.accessCode:
        return <AccessCodeScene email={email} onBack={handleCreateAccount} onSubmit={handleAccessCodeSubmit} />
      case Scenes.login.expiredPassword:
        return (
          <ExpiredPasswordScene
            password={password}
            onSubmit={handleUpdatePasswordSubmit}
            onForgotPassword={handleForgotPasswordClick}
          />
        )
      case Scenes.login.forgotPassword:
        return <ForgotPasswordScene email={email} onBack={handleBackToEmailScene} onSubmit={handleForgotPassword} />
      case Scenes.login.generateMfa:
        return <GenerateMfaScene onSubmit={handleGenerateMfa} onBack={handleBackToEmailScene} options={mfaOptions} />
      case Scenes.login.verifyMfa:
        return (
          <VerifyMfaScene
            onSubmit={handleVerifyMfa}
            onBack={handleBackToEmailScene}
            onResend={handleResendGenerateMfa}
            onReselectMethod={handleBackToGenerateMfa}
          />
        )
      case Scenes.login.createAccount:
        return <VerifyCreateAccountScene onSubmit={handleVerifyCreateAccount} onBack={handleBackToEmailScene} />
      case Scenes.login.email:
      default:
        return (
          <LoginEmailScene email={query?.email} onSubmit={handleEmailSubmit} onCreateAccount={handleCreateAccount} />
        )
    }
  }

  return (
    <div className="relative flex w-full flex-auto flex-col items-center justify-between bg-white transition-colors">
      <section className="z-4 bg-primary-700 relative w-full sm:max-h-none sm:min-h-[715px] md:min-h-[668px]">
        <div className="relative mx-auto flex h-full max-w-[1920px] flex-col gap-6 px-[30px] pb-10 pt-6 md:flex-row md:gap-2 2xl:px-[104px] 2xl:pt-[85px]">
          <img src={TopBubblesUrl} className="absolute right-0 top-0 hidden md:block" alt="" />
          <div className="w-[calc(100% - 30px)] absolute z-[2] -mx-4 -mb-[35px] flex flex-col items-center self-end sm:bottom-0 md:left-96 lg:left-[420px] xl:bottom-6 xl:left-[380px] xl:-mb-[75px] xl:-ml-12 2xl:left-[calc(50%-160px)]">
            <img
              alt=""
              src={EntryCentralGirlUrl}
              className="z-[1] hidden h-auto w-0 sm:block sm:w-96 lg:max-w-lg xl:w-[500px] xl:max-w-xl 2xl:w-[580px]"
            />
          </div>
          <div className="z-[3] order-2 flex h-full w-full flex-1 flex-col justify-start pl-0 md:w-auto md:pl-10 lg:pl-0">
            <div className="flex w-full max-w-full flex-col items-center justify-center sm:w-96 md:w-full xl:absolute xl:right-[30px] xl:top-[120px] xl:items-end 2xl:right-[104px] 2xl:top-[180px] 2xl:max-w-[480px]">
              {routes && routes.length > 0
                ? routes.map(({ path, ...route }) => <RouteBranch key={path} path={path} {...props} {...route} />)
                : viewForScene(scene)}
              <div className="block md:hidden">
                <AppBadges />
              </div>
            </div>
          </div>
          <div className="z-10 order-1 flex flex-col items-start justify-start gap-6 md:flex-1 xl:gap-0">
            <ApplicationLogo
              className="h-auto max-w-[107px] md:max-w-[140px] xl:w-full xl:max-w-[186px]"
              variant={LogoVariants.whiteText}
            />
            <div className="z-[1] w-full text-neutral-100 xl:mt-8 2xl:w-[580px]">
              <h2 className="typography-h3 md:typography-h2 2xl:typography-h1xl whitespace-normal">
                Virtual care <br /> from anywhere.
              </h2>
              <p className="typography-body mt-s md:typography-body-xl max-w-[400px] leading-5 2xl:max-w-[460px] 2xl:text-2xl">
                {APP_NAME} offers 24/7 access to virtual care, self-care, and well-being tools.
              </p>
            </div>
            <div className="hidden md:block">
              <AppBadges />
            </div>
          </div>
          <img src={TopLeafUrl} className="absolute -bottom-[25px] left-0 z-0 h-[161px]" alt="" />
        </div>
      </section>
      <section className="bg-pantone-200 relative z-0 min-h-[464px] w-full grow">
        <div className="gap-xs relative mx-auto my-0 flex h-full max-w-[1920px] flex-col items-start justify-center px-[30px] pb-[40px] pt-[24px] 2xl:px-[104px] 2xl:pb-[40px] 2xl:pt-[85px]">
          <SupportIcon className="text-primary-600 h-6 w-6" aria-hidden="true" />
          <h2 className="typography-h5 mb-xs mt-s md:typography-h4 whitespace-normal text-neutral-800">
            How can we help?
          </h2>
          <p className="typography-body-l md:typography-body-xl max-w-[427px] whitespace-normal font-medium text-neutral-800 md:max-w-[300px] lg:max-w-[427px]">
            We're committed to excellence. Contact us and let us know how we can help.
            <br />
            <br />
            For assistance, please{' '}
            <a className="text-link-primary whitespace-normal font-bold" href={`tel:+1${SUPPORT_NUMBER}`}>
              call us at 833-4-TIMELY
            </a>
            .
          </p>
          <img
            alt=""
            src={BottomLeafUrl}
            className="absolute -bottom-16 -right-32 z-0 h-48 md:right-0 md:h-56 lg:bottom-0 lg:h-auto"
          />
        </div>
      </section>
      <CookiesAgreementDialog onConfirm={handleCookiesAgreement} ref={CookiesModalRef} />
      <EntryFooter />
    </div>
  )
}

export default EntryPage
